import type { ApiResponse, AuthResponse } from '../types'
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setAccessToken,
} from './auth-storage'
import type {
  AdminDashboard,
  AdminMetricsTrends,
  AdminRiskAnalysis,
  AdminCompletionRate,
  AdminUser,
  AdminOrganizationStat,
  AdminLearningEngagement,
  AdminDropoutAnalysis,
  AdminSystemHealth,
  AdminLogEntry,
  AdminWaitlistOverview,
  AdminFeedback,
  AdminReflection,
} from '@/types/admin'

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://api-ipvp.citizensgavel.org/api/v1'
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT || '10000')

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Resolve a human-readable message from a backend error payload.
// Handles plain strings, arrays of strings (NestJS validation pipe), and the
// nested { message: { message: string | string[], error } } shape our gateway returns.
function resolveErrorMessage(errorData: unknown, status: number): string {
  const data = (errorData ?? {}) as Record<string, unknown>
  const messageField = data.message
  if (typeof messageField === 'string' && messageField.trim()) return messageField
  if (Array.isArray(messageField) && messageField.length) return messageField.join(', ')
  if (messageField && typeof messageField === 'object') {
    const inner = (messageField as Record<string, unknown>).message
    if (typeof inner === 'string' && inner.trim()) return inner
    if (Array.isArray(inner) && inner.length) return inner.join(', ')
    const innerError = (messageField as Record<string, unknown>).error
    if (typeof innerError === 'string' && innerError.trim()) return innerError
  }
  if (typeof data.error === 'string' && data.error.trim()) return data.error
  if (Array.isArray(data.errors) && data.errors.length) return (data.errors as unknown[]).join(', ')
  return `HTTP ${status}`
}

interface RequestOptions extends RequestInit {
  timeout?: number
  skipAuth?: boolean
  _isRetry?: boolean
}

class ApiClient {
  private baseURL: string
  private defaultTimeout: number

  constructor(baseURL: string = API_BASE_URL, timeout: number = API_TIMEOUT) {
    this.baseURL = baseURL
    this.defaultTimeout = timeout
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const { timeout = this.defaultTimeout, skipAuth, _isRetry, ...fetchOptions } =
      options

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        ...(fetchOptions.headers as Record<string, string>),
      }

      const accessToken = !skipAuth ? getAccessToken() : null
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`
      }

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...fetchOptions,
        signal: controller.signal,
        headers,
      })

      clearTimeout(timeoutId)

      if (response.status === 401 && !skipAuth && !_isRetry) {
        const refreshed = await this.refreshAccessToken()
        if (refreshed) {
          return this.request<T>(endpoint, {
            ...options,
            headers: { ...fetchOptions.headers },
            _isRetry: true,
          })
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new ApiError(resolveErrorMessage(errorData, response.status), response.status, errorData)
      }

      return (await response.json()) as ApiResponse<T>
    } catch (error) {
      clearTimeout(timeoutId)

      if (error instanceof ApiError) {
        throw error
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError('Request timeout', 408)
      }

      throw new ApiError('Network error', 0, error)
    }
  }

  private async refreshAccessToken(): Promise<string | null> {
    const refreshToken = getRefreshToken()
    if (!refreshToken) return null

    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      })

      if (!response.ok) {
        clearTokens()
        return null
      }

      const data = (await response.json()) as { access_token?: string }
      if (data?.access_token) {
        setAccessToken(data.access_token)
        return data.access_token
      }

      clearTokens()
      return null
    } catch {
      clearTokens()
      return null
    }
  }

  async get<T>(
    endpoint: string,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' })
  }

  async post<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    const requestOptions: RequestOptions = {
      ...options,
      method: 'POST',
    }
    if (data) {
      requestOptions.body = JSON.stringify(data)
    }
    return this.request<T>(endpoint, requestOptions)
  }

  async put<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    const requestOptions: RequestOptions = {
      ...options,
      method: 'PUT',
    }
    if (data) {
      requestOptions.body = JSON.stringify(data)
    }
    return this.request<T>(endpoint, requestOptions)
  }

  async patch<T>(
    endpoint: string,
    data?: unknown,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    const requestOptions: RequestOptions = {
      ...options,
      method: 'PATCH',
    }
    if (data) {
      requestOptions.body = JSON.stringify(data)
    }
    return this.request<T>(endpoint, requestOptions)
  }

  async delete<T>(
    endpoint: string,
    options?: RequestOptions
  ): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' })
  }

  async postFormData<T>(
    endpoint: string,
    formData: FormData,
    options?: Omit<RequestOptions, 'body'>
  ): Promise<ApiResponse<T>> {
    const { timeout = this.defaultTimeout, skipAuth, _isRetry, ...fetchOptions } = options || {}

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), timeout)

    try {
      const headers: Record<string, string> = {
        ...(fetchOptions.headers as Record<string, string>),
      }

      const accessToken = !skipAuth ? getAccessToken() : null
      if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`
      }

      const response = await fetch(`${this.baseURL}${endpoint}`, {
        ...fetchOptions,
        method: 'POST',
        signal: controller.signal,
        headers,
        body: formData,
      })

      clearTimeout(timeoutId)

      if (response.status === 401 && !skipAuth && !_isRetry) {
        const refreshed = await this.refreshAccessToken()
        if (refreshed) {
          return this.postFormData<T>(endpoint, formData, {
            ...options,
            _isRetry: true,
          } as RequestOptions)
        }
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new ApiError(resolveErrorMessage(errorData, response.status), response.status, errorData)
      }

      return (await response.json()) as ApiResponse<T>
    } catch (error) {
      clearTimeout(timeoutId)

      if (error instanceof ApiError) {
        throw error
      }

      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError('Request timeout', 408)
      }

      throw new ApiError('Network error', 0, error)
    }
  }
}

// Export a default instance
export const api = new ApiClient()
export { ApiClient, ApiError }

// Waitlist API functions
import type { WaitlistEntry, WaitlistResponse } from '../types'

export const waitlistApi = {
  async joinWaitlist(
    data: WaitlistEntry
  ): Promise<ApiResponse<WaitlistResponse>> {
    return api.post<WaitlistResponse>('/waitlist', data)
  },
}

// Inquiries API (public — no auth)
export interface PartnerInquiryPayload {
  name: string
  organization: string
  email: string
  phone?: string
  message?: string
}

export interface ContactInquiryPayload {
  fullName: string
  email: string
  message: string
}

export interface InquirySuccess {
  success: true
}

export const inquiriesApi = {
  async partner(data: PartnerInquiryPayload): Promise<ApiResponse<InquirySuccess>> {
    return api.post<InquirySuccess>('/inquiries/partner', data, { skipAuth: true })
  },
  async contact(data: ContactInquiryPayload): Promise<ApiResponse<InquirySuccess>> {
    return api.post<InquirySuccess>('/inquiries/contact', data, { skipAuth: true })
  },
}

// Auth API helpers
import type { AuthUser } from '../types'

const unwrap = <T>(response: ApiResponse<T> | T): T => {
  if (response && typeof response === 'object' && 'data' in response) {
    return (response as ApiResponse<T>).data
  }
  return response as T
}

export const authApi = {
  async sendLoginPin(email: string) {
    return api.post('/auth/send-login-pin', { email })
  },

  async verifyLoginPin(payload: { email: string; pin: string }) {
    const res = await api.post<AuthResponse>('/auth/verify-login-pin', payload)
    return unwrap(res)
  },

  async sendOtp(phoneNumber: string) {
    return api.post('/auth/send-otp', { phoneNumber })
  },

  async verifyOtp(payload: { phoneNumber: string; otp: string }) {
    return api.post<{ message: string; needsPassword?: boolean; userId?: string }>(
      '/auth/verify-otp',
      payload
    )
  },

  async createPassword(payload: {
    userId: string
    password: string
    confirmPassword: string
  }) {
    const res = await api.post<AuthResponse>('/auth/create-password', payload)
    return unwrap(res)
  },

  async getInvitationInfo(coupleId: string) {
    const res = await api.get<{
      data: {
        personOne: InvitationPerson
        personTwo: InvitationPerson
      }
    }>(`/auth/invitation/${coupleId}`)
    return unwrap(res)
  },

  async loginWithPassword(payload: { phoneNumber: string; password: string }) {
    const res = await api.post<AuthResponse>('/auth/login', payload)
    return unwrap(res)
  },

  async loginWithEmailPassword(payload: { email: string; password: string }) {
    const res = await api.post<AuthResponse>('/auth/login', payload)
    return unwrap(res)
  },

  async profile() {
    return api.get<AuthUser>('/auth/profile')
  },
}

// Admin API helpers
export const adminApi = {
  dashboard() {
    return api.get<AdminDashboard>('/admin/dashboard')
  },
  metricsTrends(params?: { period?: string; metric?: string }) {
    const search = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : ''
    return api.get<AdminMetricsTrends>(`/admin/metrics/trends${search}`)
  },
  riskAnalysis() {
    return api.get<AdminRiskAnalysis>('/admin/analytics/risk-analysis')
  },
  completionRates() {
    return api.get<AdminCompletionRate[]>('/admin/analytics/completion-rates')
  },
  users(params?: { page?: number; limit?: number; role?: string; status?: string; search?: string }) {
    const search = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : ''
    return api.get<{ data: { data: AdminUser[]; pagination?: unknown } }>(`/admin/users${search}`)
  },
  userActivity(params?: { limit?: number }) {
    const search = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : ''
    return api.get<{ data: unknown[] }>(`/admin/users/activity${search}`)
  },
  organizationsPending() {
    return api.get<{ data: AdminOrganizationStat[] }>('/admin/organizations/pending')
  },
  organizationStats() {
    return api.get<{ data: AdminOrganizationStat[] }>('/admin/organizations/stats')
  },
  inviteOrganization(payload: Record<string, unknown>) {
    return api.post('/admin/organizations/invite', payload)
  },
  approveOrganization(organizationId: string) {
    return api.post(`/admin/organizations/${organizationId}/approve`)
  },
  rejectOrganization(organizationId: string, reason?: string) {
    return api.post(`/admin/organizations/${organizationId}/reject`, { reason })
  },
  resendOrganizationInvite(organizationId: string) {
    return api.post(`/admin/organizations/${organizationId}/resend-invite`)
  },
  reactivateOrganization(organizationId: string) {
    return api.post(`/admin/organizations/${organizationId}/reactivate`)
  },
  createAdmin(payload: {
    email: string
    phoneNumber: string
    password: string
    firstName: string
    lastName: string
    notes?: string
  }) {
    return api.post('/admin/users/create-admin', payload)
  },
  updateUserStatus(userId: string, isActive: boolean) {
    return api.patch(`/admin/users/${userId}/status`, { isActive })
  },
  bulkUserAction(payload: { userIds: string[]; action: 'activate' | 'deactivate' | 'delete' }) {
    return api.post('/admin/users/bulk-action', payload)
  },
  learningEngagement() {
    return api.get<AdminLearningEngagement>('/admin/learning/engagement')
  },
  learningDropout() {
    return api.get<AdminDropoutAnalysis[]>('/admin/learning/dropout-analysis')
  },
  couplesFlagged() {
    return api.get<{ data: Array<{ name?: string; riskLevel?: string }> }>('/admin/couples/flagged')
  },
  couplesList(params?: { page?: number; limit?: number; status?: string }) {
    const search = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : ''
    return api.get(`/admin/couples${search}`)
  },
  couplesInvite(payload: Record<string, unknown>) {
    return api.post('/admin/couples/invite', payload)
  },
  /** Organization-level couple invite (non-admin) */
  couplesInviteOrg(payload: Record<string, unknown>) {
    return api.post('/couples/invite', payload)
  },
  couplesResendInvite(coupleId: string) {
    return api.post(`/couples/${coupleId}/resend-invite`, {})
  },
  coupleDetail(coupleId: string) {
    return api.get(`/couples/${coupleId}`)
  },
  systemHealth() {
    return api.get<AdminSystemHealth>('/admin/system/health')
  },
  systemLogs(params?: { level?: string; limit?: number }) {
    const search = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : ''
    return api.get<AdminLogEntry[]>(`/admin/system/logs${search}`)
  },
  reportsSummary(params: { startDate: string; endDate: string }) {
    const search = `?${new URLSearchParams(params as Record<string, string>).toString()}`
    return api.get(`/admin/reports/summary${search}`)
  },
  reportsExport(params: { type: string; format?: string }) {
    const search = `?${new URLSearchParams(params as Record<string, string>).toString()}`
    return api.get(`/admin/reports/export${search}`)
  },
  waitlistOverview() {
    return api.get<AdminWaitlistOverview>('/admin/waitlist/overview')
  },
  waitlistEntries(params?: { status?: string; page?: number; pageSize?: number }) {
    const search = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : ''
    return api.get(`/waitlist${search}`)
  },
  updateWaitlistStatus(id: number, status: 'pending' | 'contacted' | 'converted') {
    return api.patch(`/admin/waitlist/${id}/status`, { status })
  },
  contentFeedback(params?: { flagged?: string }) {
    const search = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : ''
    return api.get<AdminFeedback[]>(`/admin/content/feedback${search}`)
  },
  contentReflections(params?: { videoId?: string }) {
    const search = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : ''
    return api.get<AdminReflection[]>(`/admin/content/reflections${search}`)
  },
  deleteReflection(id: string) {
    return api.delete(`/admin/content/reflection/${id}`)
  },
  updateUser(id: string, payload: Partial<{ email: string; password: string; firstName: string; lastName: string; isActive: boolean }>) {
    return api.patch(`/users/${id}`, payload)
  },
  organizationDetail(organizationId: string) {
    return api.get(`/organizations/${organizationId}`)
  },
  organizationMembers(organizationId: string) {
    return api.get(`/organizations/${organizationId}/members`)
  },
  coupleAssessments(coupleId: string) {
    return api.get(`/admin/couples/${coupleId}/assessments`)
  },
  assessmentDetailed(assessmentId: string) {
    return api.get(`/admin/assessments/${assessmentId}/detailed`)
  },
  userAssessments(userId: string) {
    return api.get(`/admin/users/${userId}/assessments`)
  },
}

export interface InvitationPerson {
  userId: string
  firstName?: string
  lastName?: string
  phoneNumber: string
  email?: string
  hasPassword?: boolean
  isPhoneVerified?: boolean
}

export interface CoupleSummary {
  id: string
  contractNumber?: string | null
  contract?: string | null
  status?: string | null
  requestDate?: string | null
  lastEditDate?: string | null
  createdAt?: string | null
  updatedAt?: string | null
  personOne?: {
    firstName?: string | null
    lastName?: string | null
  } | null
  personTwo?: {
    firstName?: string | null
    lastName?: string | null
  } | null
}

export interface CouplePerson {
  id?: string | null
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  phoneNumber?: string | null
  dateOfBirth?: string | null
  tribe?: string | null
  genotype?: string | null
  gender?: string | null
}

export interface PerformanceCategory {
  category?: string | null
  label?: string | null
  score?: number | null
  maxScore?: number | null
  riskLevel?: string | null
  riskLabel?: string | null
  classification?: string | null
}

export interface PerformanceSummary {
  score?: number | null
  maxScore?: number | null
  riskLevel?: string | null
  riskLabel?: string | null
}

export interface PerformanceUser {
  user?: {
    id?: string | null
    name?: string | null
    gender?: string | null
  } | null
  assessmentId?: string | null
  status?: string | null
  completedAt?: string | null
  overall?: PerformanceSummary | null
  categories?: PerformanceCategory[] | null
}

export interface CouplePerformance {
  personOne?: PerformanceUser | null
  personTwo?: PerformanceUser | null
}

export interface CertificationPerson {
  eligible?: boolean | null
  status?: string | null
  assessmentId?: string | null
  overallRisk?: string | null
  overallRiskLabel?: string | null
}

export interface CoupleCertification {
  coupleStatus?: string | null
  certificationDate?: string | null
  eligible?: boolean | null
  approvalStatus?: string | null
  approvalRequired?: boolean | null
  personOne?: CertificationPerson | null
  personTwo?: CertificationPerson | null
}

export interface FeedbackResponse {
  question?: string | null
  answer?: string | null
}

export interface FeedbackPerson {
  user?: {
    id?: string | null
    name?: string | null
    gender?: string | null
  } | null
  submittedAt?: string | null
  responses?: FeedbackResponse[] | null
}

export interface CoupleFeedback {
  personOne?: FeedbackPerson | null
  personTwo?: FeedbackPerson | null
}

export interface CoupleDetail extends CoupleSummary {
  progressPercentage?: number | null
  isPersonOneCompleted?: boolean | null
  isPersonTwoCompleted?: boolean | null
  personOneGenotypeEvidence?: string | null
  personTwoGenotypeEvidence?: string | null
  personOne?: CouplePerson | null
  personTwo?: CouplePerson | null
  organization?: {
    id?: string | null
    name?: string | null
    email?: string | null
    phoneNumber?: string | null
    address?: string | null
    city?: string | null
    state?: string | null
    country?: string | null
    logo?: string | null
    website?: string | null
    status?: string | null
    registrationNumber?: string | null
    certificateDocument?: string | null
    totalInvitedCouples?: number | null
    pendingInvites?: number | null
    certifiedCouples?: number | null
    couplesInProgress?: number | null
    certificationApproval?: number | null
    createdAt?: string | null
    updatedAt?: string | null
  } | null
  performance?: CouplePerformance | null
  certification?: CoupleCertification | null
  feedback?: CoupleFeedback | null
}

export interface CoupleProgress {
  coupleId?: string | null
  status?: string | null
  progressPercentage?: number | null
  personOne?: {
    id?: string | null
    name?: string | null
    completed?: boolean | null
  } | null
  personTwo?: {
    id?: string | null
    name?: string | null
    completed?: boolean | null
  } | null
  isPersonOneCompleted?: boolean | null
  isPersonTwoCompleted?: boolean | null
  personOneName?: string | null
  personTwoName?: string | null
}

export const couplesApi = {
  list(params?: { status?: string }) {
    const search = params?.status
      ? `?${new URLSearchParams({ status: params.status }).toString()}`
      : ''
    return api.get<CoupleSummary[]>(`/couples${search}`)
  },
  detail(coupleId: string) {
    return api.get<CoupleDetail>(`/couples/${coupleId}`)
  },
  progress(coupleId: string) {
    return api.get<CoupleProgress>(`/couples/${coupleId}/progress`)
  },
}

// Analytics API
import type { UserDashboardData } from '@/types'

export interface OrganizationStatistics {
  totalInvitedCouples: number
  pendingInvites: number
  certifiedCouples: number
  couplesInProgress: number
  certificationApproval: number
}

export interface DashboardStats {
  lowRatingDistribution: Record<string, number>
  performanceOverview?: {
    totalAssessments: number
    completedAssessments: number
    averageScore: number
  }
}

export interface AgeDistribution {
  '18-25': number
  '25-30': number
  '30-40': number
  '40+': number
}

export const analyticsApi = {
  async getUserDashboard(coupleId?: string) {
    const params = coupleId ? `?coupleId=${coupleId}` : ''
    return api.get<UserDashboardData>(`/analytics/user-dashboard${params}`)
  },
  async getDashboard() {
    return api.get<DashboardStats>('/analytics/dashboard')
  },
  async getAgeDistribution() {
    return api.get<AgeDistribution>('/analytics/age-distribution')
  },
}

// Organization API
export interface CoupleAssignment {
  id: string
  counselorId: string
  coupleId: string
  organizationId?: string
  assignedById?: string
  createdAt?: string
  counselor?: {
    id: string
    firstName?: string | null
    lastName?: string | null
    email?: string | null
  }
  couple?: {
    id: string
    status?: string | null
    personOne?: { id?: string; firstName?: string | null; lastName?: string | null } | null
    personTwo?: { id?: string; firstName?: string | null; lastName?: string | null } | null
  }
}

export const organizationApi = {
  async getStatistics(organizationId: string) {
    return api.get<OrganizationStatistics>(`/organizations/${organizationId}/statistics`)
  },
  async getOrganization(organizationId: string) {
    return api.get<OrganizationDetail>(`/organizations/${organizationId}`)
  },
  inviteUser(organizationId: string, payload: {
    firstName: string
    lastName: string
    email: string
    phoneNumber: string
    role: string
    systemRole?: string
    permissions?: string[]
    invitationMessage?: string
  }) {
    return api.post(`/organizations/${organizationId}/invite-user`, payload)
  },
  // Members
  getMembers(organizationId: string) {
    return api.get<OrganizationUser[]>(`/organizations/${organizationId}/members`)
  },
  updateMemberPermissions(organizationId: string, payload: { userId: string; permissions: string[] }) {
    return api.patch(`/organizations/${organizationId}/members/permissions`, payload)
  },
  // Assignments
  getAssignments(organizationId: string) {
    return api.get<CoupleAssignment[]>(`/organizations/${organizationId}/assignments`)
  },
  getCounselorAssignments(organizationId: string, counselorId: string) {
    return api.get<CoupleAssignment[]>(`/organizations/${organizationId}/assignments/counselor/${counselorId}`)
  },
  getMyAssignedCouples(organizationId: string) {
    return api.get<CoupleAssignment[]>(`/organizations/${organizationId}/assignments/my-couples`)
  },
  assignCouples(organizationId: string, payload: { counselorId: string; coupleIds: string[] }) {
    return api.post(`/organizations/${organizationId}/assignments`, payload)
  },
  unassignCouples(organizationId: string, payload: { counselorId: string; coupleIds: string[] }) {
    return api.delete(`/organizations/${organizationId}/assignments`, {
      body: JSON.stringify(payload),
    })
  },
}

export interface OrganizationUser {
  id: string
  firstName?: string | null
  lastName?: string | null
  email?: string | null
  phoneNumber?: string | null
  role?: string | null
  userType?: string | null
  systemRole?: string | null
  permissions?: string[]
  isActive?: boolean | null
  isPhoneVerified?: boolean | null
  createdAt?: string | null
  updatedAt?: string | null
}

export interface OrganizationDetail {
  id: string
  name?: string | null
  logo?: string | null
  status?: string | null
  users?: OrganizationUser[] | null
}

export const userApi = {
  activate(userId: string) {
    return api.patch(`/users/${userId}/activate`)
  },
  deactivate(userId: string) {
    return api.patch(`/users/${userId}/deactivate`)
  },
}

// Assessments (couple)
export interface RequiredVideo {
  videoId: string
  title?: string
  description?: string
  videoUrl?: string
  duration?: number
  isWatched: boolean
  watchedSeconds?: number
  watchPercentage?: number
}

export interface RequiredModule {
  moduleId: string
  title?: string
  videos: RequiredVideo[]
}

export interface RequiredVideosResponse {
  required: boolean
  allCompleted?: boolean
  sectionNumber?: number
  category?: string
  riskLevel?: string
  modules: RequiredModule[]
}

export const assessmentApi = {
  startAssessment(coupleId: string) {
    return api.post(`/assessments/start/${coupleId}`)
  },
  listQuestions(params?: { section?: number; category?: string }) {
    const search = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : ''
    return api.get(`/questions${search}`)
  },
  submitSection(assessmentId: string, answers: Array<{ questionId: string; sectionNumber: number; answer: string; score?: number }>) {
    return api.post(`/assessments/${assessmentId}/submit-section`, { answers })
  },
  getAssessment(assessmentId: string) {
    return api.get(`/assessments/${assessmentId}`)
  },
  getResults(assessmentId: string) {
    return api.get(`/assessments/${assessmentId}/results`)
  },
  coupleAssessments(coupleId: string) {
    return api.get(`/assessments/couple/${coupleId}`)
  },
  assessmentHistory(coupleId: string) {
    return api.get(`/assessments/history/${coupleId}`)
  },
  getRequiredVideos(assessmentId: string) {
    return api.get<RequiredVideosResponse>(`/assessments/${assessmentId}/required-videos`)
  },
  updateVideoProgress(
    assessmentId: string,
    videoId: string,
    coupleId: string,
    data: { watchedSeconds: number; totalDuration: number }
  ) {
    return api.post(`/assessments/${assessmentId}/videos/${videoId}/progress/${coupleId}`, data)
  },
  getVideoProgress(assessmentId: string, videoId: string, coupleId: string) {
    return api.get(`/assessments/${assessmentId}/videos/${videoId}/progress/${coupleId}`)
  },
  createQuestion(payload: Record<string, unknown>) {
    return api.post('/questions', payload)
  },
  updateQuestion(id: string, payload: Record<string, unknown>) {
    return api.patch(`/questions/${id}`, payload)
  },
  deleteQuestion(id: string) {
    return api.delete(`/questions/${id}`)
  },
}

// Learning path
export const learningApi = {
  listModules(params?: { category?: string; recommended?: boolean }) {
    const search = params
      ? `?${new URLSearchParams(
          Object.entries(params).reduce((acc, [k, v]) => {
            if (v === undefined) return acc
            acc[k] = String(v)
            return acc
          }, {} as Record<string, string>)
        ).toString()}`
      : ''
    return api.get(`/learning-path/modules${search}`)
  },
  recommendedModules(coupleId: string) {
    return api.get(`/learning-path/recommended/${coupleId}`)
  },
  progress(coupleId: string) {
    return api.get(`/learning-path/progress/${coupleId}`)
  },
  startModule(moduleId: string, coupleId: string) {
    return api.post(`/learning-path/modules/${moduleId}/start/${coupleId}`)
  },
  completeVideo(moduleId: string, videoId: string, coupleId: string) {
    return api.post(`/learning-path/modules/${moduleId}/videos/${videoId}/complete/${coupleId}`)
  },
  updateVideoProgress(videoId: string, coupleId: string, data: { watchedSeconds: number; totalDuration: number }) {
    return api.post(`/learning-path/videos/${videoId}/progress/${coupleId}`, data)
  },
  getVideoProgress(videoId: string, coupleId: string) {
    return api.get(`/learning-path/videos/${videoId}/progress/${coupleId}`)
  },
  submitReflection(videoId: string, reflection: string) {
    return api.post(`/learning-path/videos/${videoId}/reflection`, { reflection })
  },
}

export const learningPathApi = {
  getRecommendedModules(coupleId: string) {
    return api.get(`/learning-path/recommended/${coupleId}`)
  },
  listModules(params?: { category?: string; recommended?: boolean }) {
    const search = params
      ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
      : ''
    return api.get(`/learning-path/modules${search}`)
  },
  getModule(moduleId: string) {
    return api.get(`/learning-path/modules/${moduleId}`)
  },
  startModule(moduleId: string, coupleId: string) {
    return api.post(`/learning-path/modules/${moduleId}/start/${coupleId}`)
  },
  // Admin/Super Admin endpoints
  createModule(data: {
    title: string
    description?: string
    category: string
    orderIndex: number
    estimatedDuration: number
    isRecommended?: boolean
    videos?: Array<{
      title: string
      description?: string
      videoUrl: string
      thumbnail?: string
      duration: number
      orderIndex: number
    }>
  }) {
    return api.post('/learning-path/modules', data)
  },
  updateModule(moduleId: string, data: Partial<{
    title: string
    description: string
    category: string
    orderIndex: number
    estimatedDuration: number
    isRecommended: boolean
    thumbnail: string
    videos: Array<{
      title: string
      description?: string
      videoUrl: string
      thumbnail?: string
      duration: number
      orderIndex: number
    }>
  }>) {
    return api.patch(`/learning-path/modules/${moduleId}`, data)
  },
  deleteModule(moduleId: string) {
    return api.delete(`/learning-path/modules/${moduleId}`)
  },
  uploadThumbnail(_moduleId: string, file: File) {
    const formData = new FormData()
    formData.append('file', file)
    return api.postFormData(`/files/upload?isPublic=true`, formData)
  },
  uploadVideo(_videoId: string, file: File) {
    const formData = new FormData()
    formData.append('file', file)
    return api.postFormData(`/files/upload?isPublic=true`, formData)
  },
  addVideo(moduleId: string, data: {
    title: string
    description?: string
    videoUrl: string
    thumbnail?: string
    duration: number
    orderIndex: number
  }) {
    return api.post(`/learning-path/modules/${moduleId}/videos`, data)
  },
  updateVideo(videoId: string, data: Partial<{
    title: string
    description: string
    videoUrl: string
    duration: number
    orderIndex: number
  }>) {
    return api.patch(`/learning-path/videos/${videoId}`, data)
  },
  deleteVideo(videoId: string) {
    return api.delete(`/learning-path/videos/${videoId}`)
  },
  addVideoToCategory(category: string, data: {
    title: string
    description?: string
    videoUrl: string
    duration?: number
    orderIndex?: number
  }) {
    return api.post(`/learning-path/categories/${category}/videos`, data)
  },
}
